import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Switch, { SwitchClickHandler } from '@splunk/react-ui/Switch';
import { ThemeProvider, useLegacyThemeServiceKey } from '../../../../common/theme/ThemeProvider';
import P from '@splunk/react-ui/Paragraph';
import { EntityTypeSelect } from '@splunk/olly-imm';
import { isEqual } from 'lodash';
import { useEntityTypes } from '@splunk/olly-imm/build/entity/hooks/useEntityTypes';
import {
    EntityType,
    EntityTypeMatchCategory,
} from '@splunk/olly-imm/build/entity/model/EntityType';
import { variables } from '@splunk/themes';

interface EntityTypeSummary {
    contributing: boolean;
    types: string[];
}

interface EntityHealthContentProps {
    entityTypeSummary?: EntityTypeSummary;
    isNewDetector: boolean;
    onChange: (entityTypeSummary: EntityTypeSummary) => void;
    dimensionsMap?: Map<string, Set<string>>;
}

const Container = styled.div`
    padding: 8px;
`;

const EntityContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: ${variables.spacingSmall};
`;

const getFirstSuggestedEntityTypeIdentifier = (entityTypes: EntityType[]): string | undefined =>
    entityTypes.find(
        (entityType) =>
            entityType.matchCategory === EntityTypeMatchCategory.PREFERRED ||
            entityType.matchCategory === EntityTypeMatchCategory.DERIVABLE
    )?.identifier;

const EntityHealthContent: React.FC<EntityHealthContentProps> = ({
    entityTypeSummary,
    isNewDetector,
    onChange,
    dimensionsMap,
}): JSX.Element => {
    const initialEntityType = entityTypeSummary?.types[0];
    const initialContributing = entityTypeSummary?.contributing ?? isNewDetector;
    const [currentEntityType, setCurrentEntityType] = useState(initialEntityType);
    const [attachToEntityHealth, setAttachToEntityHealth] = useState<boolean>(initialContributing);
    const [dimensionsMapChanged, setDimensionsMapChanged] = useState(false);
    const hasHandledInitialSuggestion = useRef(false);

    const prevCount = useRef<Map<string, Set<string>>>();

    const themeKey = useLegacyThemeServiceKey();

    const onEntityChange = (newValue: string | undefined): void => {
        setCurrentEntityType(newValue);
        onChange({
            contributing: attachToEntityHealth,
            types: newValue ? [newValue] : [],
        });
    };

    useEffect(() => {
        setCurrentEntityType(entityTypeSummary?.types[0]);
        setAttachToEntityHealth(entityTypeSummary?.contributing ?? isNewDetector);
    }, [entityTypeSummary, isNewDetector]);

    useEffect(() => {
        if (!isEqual(prevCount.current, dimensionsMap)) {
            const isNotInitialLoad = !!prevCount.current && prevCount.current.size > 0;
            prevCount.current = dimensionsMap;

            if (isNotInitialLoad) {
                setDimensionsMapChanged((prev) => !prev);
            }
        }
    }, [dimensionsMap]);

    const dimensions = React.useMemo(() => {
        if (!dimensionsMap || dimensionsMap.size === 0) return [];
        const allDims = new Set<string>();
        dimensionsMap.forEach((dims) => {
            dims.forEach((dim) => allDims.add(dim));
        });
        return Array.from(allDims);
    }, [dimensionsMap]);

    const metrics = React.useMemo(() => {
        if (!dimensionsMap || dimensionsMap.size === 0) return [];
        return Array.from(dimensionsMap.keys());
    }, [dimensionsMap]);
    const hasEntityTypeContext = dimensions.length > 0 || metrics.length > 0;

    const { entityTypes, isLoading } = useEntityTypes(
        hasEntityTypeContext ? { dimensions, metrics } : undefined
    );

    useEffect(() => {
        if (isLoading) {
            return;
        }

        const shouldApplyInitialSuggestion =
            !hasHandledInitialSuggestion.current && isNewDetector && hasEntityTypeContext;

        if (!shouldApplyInitialSuggestion && !dimensionsMapChanged) {
            return;
        }

        if (attachToEntityHealth) {
            const suggestedEntityType = getFirstSuggestedEntityTypeIdentifier(entityTypes);

            if (suggestedEntityType !== currentEntityType) {
                setCurrentEntityType(suggestedEntityType);
                onChange({
                    contributing: true,
                    types: suggestedEntityType ? [suggestedEntityType] : [],
                });
            }
        }

        hasHandledInitialSuggestion.current = true;
        setDimensionsMapChanged(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        attachToEntityHealth,
        currentEntityType,
        dimensionsMapChanged,
        entityTypes,
        hasEntityTypeContext,
        isLoading,
        isNewDetector,
        onChange,
    ]);

    const handleToggleValue: SwitchClickHandler = (): void => {
        if (attachToEntityHealth) {
            setAttachToEntityHealth(false);
            setCurrentEntityType(undefined);
            onChange({
                contributing: false,
                types: [],
            });
        } else {
            const suggestedEntityType =
                currentEntityType || getFirstSuggestedEntityTypeIdentifier(entityTypes);
            setAttachToEntityHealth(true);
            setCurrentEntityType(suggestedEntityType);
            onChange({
                contributing: true,
                types: suggestedEntityType ? [suggestedEntityType] : [],
            });
        }
    };

    return (
        <ThemeProvider colorScheme={themeKey}>
            <Container>
                <P>
                    The signals selected will influence the type of entity suggestions offered
                    below. Attaching a detector to an entity&#39;s health means it will affect the
                    health of a targeted entity, such as a service.
                </P>

                <EntityContainer>
                    <Switch
                        selected={attachToEntityHealth}
                        onClick={handleToggleValue}
                        appearance="toggle"
                    >
                        Attach detector to entity health
                    </Switch>

                    {attachToEntityHealth && (
                        <EntityTypeSelect
                            onChange={onEntityChange}
                            value={currentEntityType}
                            entityTypes={entityTypes}
                        />
                    )}
                </EntityContainer>
            </Container>
        </ThemeProvider>
    );
};

export default EntityHealthContent;
